import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import NotesEditor from '../components/NotesEditor';
import { STAGES, QUOTE_STATUSES, OPEN_STAGES } from '../components/crm/crmConstants';

const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€' };
// Döviz malzeme kutuları ve satır renkleri; işçilik her zaman TL
const FX_CURRENCIES = [
  { code: 'USD', rateField: 'exchange_rate', box: 'bg-blue-50 border-blue-200', title: 'text-blue-600', value: 'text-blue-700', total: 'text-blue-800', border: 'border-blue-200' },
  { code: 'EUR', rateField: 'eur_exchange_rate', box: 'bg-purple-50 border-purple-200', title: 'text-purple-600', value: 'text-purple-700', total: 'text-purple-800', border: 'border-purple-200' },
];
const ROW_BG = { USD: 'bg-blue-50', EUR: 'bg-purple-50' };
const SELECT_STYLE = {
  USD: 'bg-blue-100 text-blue-700 border-blue-300',
  EUR: 'bg-purple-100 text-purple-700 border-purple-300',
};

export default function QuoteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [searchParams] = useSearchParams();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerPrices, setCustomerPrices] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyCustomerPrices, setOnlyCustomerPrices] = useState(false);
  const [form, setForm] = useState({
    customer_id: '',
    opportunity_id: '',
    status: 'draft',
    project_name: '',
    quote_date: new Date().toISOString().split('T')[0],
    valid_until: '',
    currency: 'USD',
    exchange_rate: 1.0,
    eur_exchange_rate: 1.0,
    payment_terms: '30 gün',
    delivery_time: '15 iş günü',
    notes: '',
    vat_rate: 20,
    items: []
  });

  useEffect(() => {
    const fetchData = async () => {
      const [c, p] = await Promise.all([
        api.get('/api/customers/'),
        api.get('/api/products/')
      ]);
      setCustomers(c.data);
      setProducts(p.data);
      if (!isNew) {
        const res = await api.get(`/api/quotes/${id}`);
        setForm({ ...res.data, opportunity_id: res.data.opportunity_id || '', eur_exchange_rate: res.data.eur_exchange_rate ?? 1.0 });
        if (res.data.customer_id) {
          loadCustomerPrices(res.data.customer_id);
          loadOpportunities(res.data.customer_id);
        }
      } else {
        // Prefill when coming from a customer page or pipeline card
        const qCustomer = searchParams.get('customer_id');
        const qOpportunity = searchParams.get('opportunity_id');
        if (qCustomer) {
          const customer = c.data.find(x => String(x.id) === qCustomer);
          const opps = await loadOpportunities(qCustomer);
          const opp = qOpportunity ? opps.find(o => String(o.id) === qOpportunity) : null;
          setForm(f => ({
            ...f,
            customer_id: qCustomer,
            opportunity_id: opp ? String(opp.id) : '',
            project_name: opp ? opp.title : f.project_name,
            currency: customer?.currency || f.currency,
          }));
          loadCustomerPrices(qCustomer);
        }
      }
    };
    fetchData();
  }, [id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOpportunities = async (customerId) => {
    if (!customerId) { setOpportunities([]); return []; }
    try {
      const res = await api.get(`/api/crm/opportunities?customer_id=${customerId}`);
      setOpportunities(res.data);
      return res.data;
    } catch {
      setOpportunities([]);
      return [];
    }
  };

  const loadCustomerPrices = async (customerId) => {
    if (!customerId) return;
    try {
      const res = await api.get(`/api/customer-prices/customer/${customerId}`);
      setCustomerPrices(res.data);
    } catch {
      setCustomerPrices([]);
    }
  };

  const handleCustomerChange = (customerId) => {
    setForm({ ...form, customer_id: customerId, opportunity_id: '' });
    setOnlyCustomerPrices(false);
    setCustomerPrices([]);
    loadCustomerPrices(customerId);
    loadOpportunities(customerId);
  };

  const handleOpportunityChange = (opportunityId) => {
    const opp = opportunities.find(o => String(o.id) === String(opportunityId));
    setForm({
      ...form,
      opportunity_id: opportunityId,
      project_name: form.project_name || (opp ? opp.title : ''),
    });
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, {
        product_id: null, description: '', quantity: 1,
        unit: 'Adet', unit_purchase_price: 0, unit_sale_price: 0,
        item_currency: 'TRY', labor_price: 0,
        notes: '', section_title: false
      }]
    });
  };

  const addSection = () => {
    setForm({
      ...form,
      items: [...form.items, {
        product_id: null, description: 'YENİ BÖLÜM BAŞLIĞI',
        quantity: 0, unit: '-', unit_purchase_price: 0, unit_sale_price: 0,
        item_currency: 'TRY', labor_price: 0,
        notes: '', section_title: true
      }]
    });
  };

  const removeItem = (index) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const items = [...form.items];
    items[index] = { ...items[index], [field]: value };
    setForm({ ...form, items });
  };

  const moveItem = (index, direction) => {
    const items = [...form.items];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setForm({ ...form, items });
  };

  const selectProduct = (index, productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    if (!product) return;
    const customerPrice = customerPrices.find(cp => cp.product_id === product.id);
    const salePrice = customerPrice ? customerPrice.sale_price : product.default_sale_price;
    const items = [...form.items];
    items[index] = {
      ...items[index],
      product_id: product.id,
      description: product.name,
      brand: null,
      unit: product.unit,
      unit_purchase_price: product.default_purchase_price,
      unit_sale_price: salePrice,
      item_currency: product.currency || 'TRY',
      labor_price: customerPrice?.labor_price ?? (product.default_labor_price || 0),
    };
    setForm({ ...form, items });
  };

  // Müşteriye özel fiyatlı ürünler üstte; filtre açıksa yalnızca onlar
  const customerPriceMap = new Map(customerPrices.map(cp => [cp.product_id, cp]));
  const productMap = new Map(products.map(p => [p.id, p]));
  const brands = [...new Set([...products.map(p => p.brand), ...form.items.map(it => it.brand)]
    .map(b => (b || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr'));
  const productOptions = products
    .filter(p => !onlyCustomerPrices || customerPriceMap.has(p.id))
    .sort((a, b) => (customerPriceMap.has(b.id) ? 1 : 0) - (customerPriceMap.has(a.id) ? 1 : 0));

  const realItems = form.items.filter(i => !i.section_title);

  // Bir dövizin malzeme toplamı (USD, EUR veya TRY)
  const calcMalzeme = (cur) => realItems
    .filter(i => (i.item_currency || 'TRY') === cur)
    .reduce((s, i) => s + (parseFloat(i.unit_sale_price) || 0) * (parseFloat(i.quantity) || 0), 0);

  const calcMalzemeTRY = () => calcMalzeme('TRY');

  // TRY işçilik toplamı (tüm satırların işçilik kısmı — her zaman TRY)
  const calcIscilikTRY = () => realItems
    .reduce((s, i) => s + (parseFloat(i.labor_price) || 0) * (parseFloat(i.quantity) || 0), 0);

  const calcTotalTRY = () => calcMalzemeTRY() + calcIscilikTRY();

  const calcPurchase = () => realItems
    .reduce((s, i) => s + (parseFloat(i.unit_purchase_price) || 0) * (parseFloat(i.quantity) || 0), 0);

  const usedFx = FX_CURRENCIES.filter(fx => realItems.some(i => i.item_currency === fx.code));
  const hasUSD = usedFx.some(fx => fx.code === 'USD');
  const hasEUR = usedFx.some(fx => fx.code === 'EUR');
  const hasTRY = realItems.some(i => (i.item_currency || 'TRY') === 'TRY') || realItems.some(i => (parseFloat(i.labor_price) || 0) > 0);
  const isMixed = usedFx.length + (hasTRY ? 1 : 0) > 1;

  const rateOf = (fx) => parseFloat(form[fx.rateField]) || 1;
  const subtotalTLBase = calcTotalTRY() + usedFx.reduce((s, fx) => s + calcMalzeme(fx.code) * rateOf(fx), 0);
  const calcProfit = () => subtotalTLBase - calcPurchase();
  const calcMargin = () => subtotalTLBase > 0 ? (calcProfit() / subtotalTLBase * 100) : 0;

  // Notlar: her not ayrı satır; backend'de satır sonuyla ayrılmış tek metin olarak saklanır

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        customer_id: parseInt(form.customer_id),
        opportunity_id: form.opportunity_id ? parseInt(form.opportunity_id) : null,
        valid_until: form.valid_until || null,
        notes: (form.notes || '').split('\n').map(n => n.trim()).filter(Boolean).join('\n') || null,
        items: form.items.map(item => ({
          ...item,
          product_id: item.product_id || null,
          notes: item.notes || null,
          quantity: parseFloat(item.quantity) || 0,
          unit_purchase_price: parseFloat(item.unit_purchase_price) || 0,
          unit_sale_price: parseFloat(item.unit_sale_price) || 0,
          labor_price: parseFloat(item.labor_price) || 0,
          item_currency: item.item_currency || 'TRY',
        }))
      };
      if (isNew) {
        await api.post('/api/quotes/', payload);
      } else {
        await api.put(`/api/quotes/${id}`, payload);
      }
      navigate('/quotes');
    } catch (err) {
      alert('Hata: ' + JSON.stringify(err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{isNew ? 'Yeni Teklif' : 'Teklif Düzenle'}</h2>
        <button onClick={() => navigate('/quotes')} className="text-gray-500 hover:text-gray-700">← Geri</button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="font-semibold text-gray-700 mb-4">Teklif Bilgileri</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Müşteri *</label>
              <select required value={form.customer_id} onChange={e => handleCustomerChange(e.target.value)} className="w-full border rounded px-3 py-2">
                <option value="">Seçiniz</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proje Adı</label>
              <input value={form.project_name || ''} onChange={e => setForm({...form, project_name: e.target.value})} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fırsat (CRM)
                {form.opportunity_id && (
                  <a href={`/customers/${form.customer_id}`} onClick={e => { e.preventDefault(); navigate(`/customers/${form.customer_id}`); }} className="ml-2 text-xs text-blue-600 hover:underline">müşteri kartı →</a>
                )}
              </label>
              <select value={form.opportunity_id || ''} onChange={e => handleOpportunityChange(e.target.value)} className="w-full border rounded px-3 py-2" disabled={!form.customer_id}>
                <option value="">— Bağlı değil —</option>
                {opportunities
                  .filter(o => OPEN_STAGES.includes(o.stage) || String(o.id) === String(form.opportunity_id))
                  .map(o => <option key={o.id} value={o.id}>{o.title} ({STAGES.find(s => s.key === o.stage)?.label})</option>)}
              </select>
            </div>
            {!isNew && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
                <select value={form.status || 'draft'} onChange={e => setForm({...form, status: e.target.value})} className="w-full border rounded px-3 py-2">
                  {QUOTE_STATUSES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                </select>
                {form.status === 'approved' && form.opportunity_id && (
                  <p className="text-xs text-green-600 mt-1">Kaydedince bağlı fırsat "Kazanıldı" olarak işaretlenir.</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                USD/TL Kuru {isMixed && hasUSD && <span className="text-orange-500 font-bold ml-1">← Karma teklif</span>}
              </label>
              <input type="number" step="0.01" value={form.exchange_rate}
                onChange={e => setForm({...form, exchange_rate: parseFloat(e.target.value)})}
                className={`w-full border rounded px-3 py-2 ${isMixed && hasUSD ? 'border-orange-400 bg-orange-50' : ''}`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                EUR/TL Kuru {isMixed && hasEUR && <span className="text-orange-500 font-bold ml-1">← Karma teklif</span>}
              </label>
              <input type="number" step="0.01" value={form.eur_exchange_rate}
                onChange={e => setForm({...form, eur_exchange_rate: parseFloat(e.target.value)})}
                className={`w-full border rounded px-3 py-2 ${isMixed && hasEUR ? 'border-orange-400 bg-orange-50' : ''}`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teklif Tarihi *</label>
              <input type="date" required value={form.quote_date} onChange={e => setForm({...form, quote_date: e.target.value})} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Geçerlilik Tarihi</label>
              <input type="date" value={form.valid_until || ''} onChange={e => setForm({...form, valid_until: e.target.value})} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">KDV Oranı %</label>
              <input type="number" value={form.vat_rate} onChange={e => setForm({...form, vat_rate: parseFloat(e.target.value)})} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ödeme Şartları</label>
              <input value={form.payment_terms || ''} onChange={e => setForm({...form, payment_terms: e.target.value})} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teslim Süresi</label>
              <input value={form.delivery_time || ''} onChange={e => setForm({...form, delivery_time: e.target.value})} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ana Para Birimi (PDF)</label>
              <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="USD">USD</option>
                <option value="TRY">TRY</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div className="col-span-3">
              <NotesEditor kind="quote" value={form.notes} onChange={notes => setForm({ ...form, notes })} />
            </div>
          </div>
        </div>

        {customerPrices.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4 text-sm text-purple-700">
            Bu müşteri için {customerPrices.length} adet özel fiyat tanımlı — ürün seçiminde otomatik uygulanacak.
          </div>
        )}

        {isMixed && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 text-sm text-orange-700">
            ⚠️ Karma fiyatlandırma aktif — {usedFx.map(fx => fx.code).join(' / ')} malzeme ve TRY tutarlar PDF'de ayrı gösterilir.
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Kalemler</h3>
            <div className="flex gap-2 items-center">
              <label className={`flex items-center gap-1 text-sm mr-2 ${customerPrices.length ? 'text-purple-700' : 'text-gray-400'}`}>
                <input type="checkbox" checked={onlyCustomerPrices} disabled={!customerPrices.length}
                  onChange={e => setOnlyCustomerPrices(e.target.checked)} />
                Sadece müşteriye özel fiyatlılar ({customerPrices.length})
              </label>
              <button type="button" onClick={addSection} className="bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 text-sm font-medium">+ Bölüm Başlığı</button>
              <button type="button" onClick={addItem} className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm font-medium">+ Kalem Ekle</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 w-10"></th>
                  <th className="text-left px-2 py-2 text-gray-500">Ürün</th>
                  <th className="text-left px-2 py-2 text-gray-500">Açıklama *</th>
                  <th className="text-left px-2 py-2 text-gray-500">Marka</th>
                  <th className="text-left px-2 py-2 text-gray-500">Miktar</th>
                  <th className="text-left px-2 py-2 text-gray-500">Birim</th>
                  <th className="text-left px-2 py-2 text-gray-500">Malzeme Dövizi</th>
                  <th className="text-right px-2 py-2 text-gray-500">Malzeme Br.Fiyat</th>
                  <th className="text-right px-2 py-2 text-gray-500">İşçilik Br.Fiyat ₺</th>
                  <th className="text-right px-2 py-2 text-gray-500">Alış</th>
                  <th className="text-right px-2 py-2 text-gray-500">Malzeme Toplam</th>
                  <th className="text-right px-2 py-2 text-gray-500">İşçilik Toplam</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {form.items.map((item, i) => (
                  item.section_title ? (
                    <tr key={i} className="bg-gray-700">
                      <td className="px-2 py-2">
                        <div className="flex flex-col gap-0.5">
                          <button type="button" onClick={() => moveItem(i, -1)} className="text-gray-300 hover:text-white text-xs">▲</button>
                          <button type="button" onClick={() => moveItem(i, 1)} className="text-gray-300 hover:text-white text-xs">▼</button>
                        </div>
                      </td>
                      <td colSpan={10} className="px-3 py-2">
                        <input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)}
                          className="w-full bg-transparent text-white font-bold uppercase tracking-wider border-b border-gray-500 focus:outline-none" />
                      </td>
                      <td></td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => removeItem(i)} className="text-red-300 hover:text-red-100 font-bold text-lg">×</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className={`align-top hover:bg-gray-50 ${ROW_BG[item.item_currency] || ''}`}>
                      <td className="px-2 py-2">
                        <div className="flex flex-col gap-0.5">
                          <button type="button" onClick={() => moveItem(i, -1)} className="text-gray-400 hover:text-gray-600 text-xs">▲</button>
                          <button type="button" onClick={() => moveItem(i, 1)} className="text-gray-400 hover:text-gray-600 text-xs">▼</button>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <select value={item.product_id || ''} onChange={e => selectProduct(i, e.target.value)} className="border rounded px-1 py-1 text-xs w-28">
                          <option value="">-- Seç --</option>
                          {productOptions.map(p => {
                            const cp = customerPriceMap.get(p.id);
                            return (
                              <option key={p.id} value={p.id}>
                                {cp ? `★ ${p.name} (${cp.sale_price} ${cp.currency})` : p.name}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <input required value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} className="border rounded px-2 py-1 w-44" />
                        {!item.product_id && item.description?.trim() && (
                          <div className="text-xs text-amber-600 mt-0.5">Kaydedince Ürünler'e eklenecek</div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <input value={item.brand ?? productMap.get(item.product_id)?.brand ?? ''}
                          onChange={e => updateItem(i, 'brand', e.target.value)}
                          list="quote-brands-list" className="border rounded px-2 py-1 w-28" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.5" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseFloat(e.target.value))} className="border rounded px-2 py-1 w-16" />
                      </td>
                      <td className="px-2 py-2">
                        <select value={item.unit} onChange={e => updateItem(i, 'unit', e.target.value)} className="border rounded px-1 py-1 text-xs">
                          {['Adet', 'Metre', 'Saat', 'Gün', 'Adam/Gün', 'Paket', 'Set', 'Kutu'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <select value={item.item_currency} onChange={e => updateItem(i, 'item_currency', e.target.value)}
                          className={`border rounded px-1 py-1 text-xs font-bold ${SELECT_STYLE[item.item_currency] || 'bg-gray-100 text-gray-700'}`}>
                          <option value="TRY">₺ TRY</option>
                          <option value="USD">$ USD</option>
                          <option value="EUR">€ EUR</option>
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">{CURRENCY_SYMBOLS[item.item_currency] || '₺'}</span>
                          <input type="number" step="0.01" value={item.unit_sale_price}
                            onChange={e => updateItem(i, 'unit_sale_price', parseFloat(e.target.value))}
                            className="border rounded px-2 py-1 w-24 text-right" />
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">₺</span>
                          <input type="number" step="0.01" value={item.labor_price}
                            onChange={e => updateItem(i, 'labor_price', parseFloat(e.target.value))}
                            className="border rounded px-2 py-1 w-24 text-right"
                            placeholder="0" />
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.01" value={item.unit_purchase_price}
                          onChange={e => updateItem(i, 'unit_purchase_price', parseFloat(e.target.value))}
                          className="border rounded px-2 py-1 w-24 text-right" />
                      </td>
                      <td className="px-2 py-2 text-right font-medium whitespace-nowrap">
                        {(parseFloat(item.unit_sale_price)||0) > 0
                          ? <span className={FX_CURRENCIES.find(fx => fx.code === item.item_currency)?.value || ''}>
                              {CURRENCY_SYMBOLS[item.item_currency] || '₺'}{((parseFloat(item.unit_sale_price)||0) * (parseFloat(item.quantity)||0)).toFixed(2)}
                            </span>
                          : <span className="text-gray-300">—</span>
                        }
                      </td>
                      <td className="px-2 py-2 text-right font-medium whitespace-nowrap">
                        {(parseFloat(item.labor_price)||0) > 0
                          ? <span className="text-green-700">₺{((parseFloat(item.labor_price)||0) * (parseFloat(item.quantity)||0)).toFixed(2)}</span>
                          : <span className="text-gray-300">—</span>
                        }
                      </td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700 font-bold text-lg">×</button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
            <datalist id="quote-brands-list">
              {brands.map(b => <option key={b} value={b} />)}
            </datalist>
            {form.items.length === 0 && (
              <div className="text-center py-8 text-gray-400">Kalem veya bölüm başlığı eklemek için yukarıdaki butonları kullanın</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-end">
            <div className="w-80">
              {usedFx.map(fx => {
                const sym = CURRENCY_SYMBOLS[fx.code];
                const sub = calcMalzeme(fx.code);
                return (
                  <div key={fx.code} className={`border rounded p-3 mb-3 ${fx.box}`}>
                    <div className={`text-xs font-bold uppercase mb-2 ${fx.title}`}>{fx.code} Malzeme</div>
                    <div className="flex justify-between py-1 text-sm">
                      <span className="text-gray-600">Ara Toplam</span>
                      <span className={`font-medium ${fx.value}`}>{sym}{sub.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1 text-sm">
                      <span className="text-gray-600">KDV (%{form.vat_rate})</span>
                      <span className={fx.value}>{sym}{(sub * form.vat_rate / 100).toFixed(2)}</span>
                    </div>
                    <div className={`flex justify-between py-1 text-sm font-bold border-t mt-1 pt-1 ${fx.border}`}>
                      <span>{fx.code} Toplam</span>
                      <span className={fx.total}>{sym}{(sub * (1 + form.vat_rate / 100)).toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
              {hasTRY && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
                  <div className="text-xs font-bold text-gray-600 uppercase mb-2">TRY (Malzeme + İşçilik)</div>
                  {calcMalzemeTRY() > 0 && (
                    <div className="flex justify-between py-0.5 text-xs text-gray-500">
                      <span>Malzeme</span><span>₺{calcMalzemeTRY().toFixed(2)}</span>
                    </div>
                  )}
                  {calcIscilikTRY() > 0 && (
                    <div className="flex justify-between py-0.5 text-xs text-gray-500">
                      <span>İşçilik</span><span>₺{calcIscilikTRY().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-1 text-sm border-t border-gray-200 mt-1 pt-1">
                    <span className="text-gray-600">Ara Toplam</span>
                    <span className="font-medium">₺{calcTotalTRY().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600">KDV (%{form.vat_rate})</span>
                    <span>₺{(calcTotalTRY() * form.vat_rate / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-1 text-sm font-bold border-t border-gray-200 mt-1 pt-1">
                    <span>TRY Toplam</span>
                    <span>₺{(calcTotalTRY() * (1 + form.vat_rate / 100)).toFixed(2)}</span>
                  </div>
                </div>
              )}
              {isMixed && usedFx.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3 text-xs text-orange-700">
                  {usedFx.map(fx => (
                    <div key={fx.code}>
                      Karlılık: {CURRENCY_SYMBOLS[fx.code]}{calcMalzeme(fx.code).toFixed(2)} × {rateOf(fx)} = ₺{(calcMalzeme(fx.code) * rateOf(fx)).toFixed(2)}
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t pt-3">
                <div className="flex justify-between py-1 text-sm text-gray-500">
                  <span>Toplam Alış</span><span>₺{calcPurchase().toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-gray-500">Brüt Kâr</span>
                  <span className="text-green-600 font-medium">₺{calcProfit().toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-gray-500">Kâr Marjı</span>
                  <span className="text-green-600 font-bold">%{calcMargin().toFixed(1)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={loading} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          <button type="button" onClick={() => navigate('/quotes')} className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 font-medium">
            İptal
          </button>
        </div>
      </form>
    </div>
  );
}
